import * as React from "react";
import { ClipboardCopy, Check } from "lucide-react";
import { Button } from "./ui/button";
import { formatCapturedErrors, getCapturedErrors } from "@/utils/error-reporting";

/* global navigator, document, setTimeout */

interface CopyDiagnosticsButtonProps {
  variant?: "outline" | "ghost";
  size?: "sm" | "default";
  className?: string;
}

/**
 * Puts this session's captured errors somewhere the customer can send them.
 *
 * A task pane has no visible console and no log file, so when the add-in
 * misbehaves support has historically had nothing beyond "it went blank". This
 * gives a customer one click instead of being talked through developer tools.
 *
 * Two deliberate choices:
 *  - No toast. This is also rendered by the root error boundary, which sits
 *    OUTSIDE the providers, so there is no Toaster mounted there and a toast
 *    would silently do nothing. Feedback is inline instead.
 *  - A visible fallback. Clipboard access is unreliable in an Office WebView
 *    (permission is often refused on Mac), and inside a focus-trapped menu the
 *    hidden-textarea trick loses its selection before the copy runs. When the
 *    copy cannot be done for the user, the text is shown for them to copy.
 *
 * It copies only what error-reporting captured, which is already redacted —
 * never document text or chat content.
 */
export const CopyDiagnosticsButton: React.FC<CopyDiagnosticsButtonProps> = ({
  variant = "outline",
  size = "sm",
  className,
}) => {
  const [copied, setCopied] = React.useState(false);
  const [manualText, setManualText] = React.useState<string | null>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);

  const handleCopy = React.useCallback(() => {
    const report = buildReport();
    void copyText(report).then((ok) => {
      if (ok) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        return;
      }
      // Could not copy for them — show it so they can copy it themselves.
      setManualText(report);
    });
  }, []);

  React.useEffect(() => {
    if (manualText !== null) textareaRef.current?.select();
  }, [manualText]);

  if (manualText !== null) {
    return (
      <div className="flex flex-col gap-1 w-full">
        <p className="text-xs text-neutral-500">Select all and copy, then send this to support.</p>
        <textarea
          ref={textareaRef}
          readOnly
          value={manualText}
          className="w-full h-32 text-[10px] font-mono border rounded p-1"
        />
      </div>
    );
  }

  return (
    <Button variant={variant} size={size} className={className} onClick={handleCopy}>
      {copied ? <Check className="size-4 mr-1" /> : <ClipboardCopy className="size-4 mr-1" />}
      {copied ? "Copied" : "Copy diagnostics"}
    </Button>
  );
};

/** Build the text a customer pastes to support. */
export function buildReport(): string {
  const errors = getCapturedErrors();
  const header = [
    `Quillio Word add-in diagnostics`,
    `Generated: ${new Date().toISOString()}`,
    `Version: ${process.env.APP_VERSION ?? "unknown"}`,
    `Environment: ${process.env.APP_ENV ?? "unknown"}`,
    `Errors recorded: ${errors.length}`,
  ].join("\n");

  return `${header}\n\n${formatCapturedErrors()}`;
}

/** Try the clipboard API, then the legacy fallback. Returns false if neither worked. */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator?.clipboard?.writeText != null) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Permission refused or unavailable — try the older approach.
  }

  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}

export default CopyDiagnosticsButton;
